import { useCallback, useEffect, useState } from 'react'
import { adminClient, type AdminUserSummary, type RoleRow } from '@/auth/adminClient'
import { Modal, inputStyle, labelStyle, btnPrimary, btnGhost } from './modalShared'

type RoleGrantRow = {
  id: string
  role_name: string
  resource_type: string | null
  resource_id: string | null
}

const RESOURCE_TYPES: Array<{ value: '' | 'organisation' | 'operator' | 'stream'; label: string }> = [
  { value: '', label: 'Platform-wide' },
  { value: 'organisation', label: 'Organisation' },
  { value: 'operator', label: 'Operator' },
  { value: 'stream', label: 'Stream' },
]

export default function ManageRolesModal({
  user,
  roles,
  onClose,
  onChanged,
}: {
  user: AdminUserSummary
  roles: RoleRow[]
  onClose: () => void
  onChanged: () => void | Promise<void>
}) {
  const [grants, setGrants] = useState<RoleGrantRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pendingRole, setPendingRole] = useState<string>('')
  const [pendingResourceType, setPendingResourceType] =
    useState<'' | 'organisation' | 'operator' | 'stream'>('')
  const [pendingResourceId, setPendingResourceId] = useState<string>('')
  const [granting, setGranting] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await adminClient.listUserRoles(user.id)
      setGrants(r.roles.map((g) => ({
        id: g.id, role_name: g.role_name,
        resource_type: g.resource_type, resource_id: g.resource_id,
      })))
    } catch (e) {
      setError(`Failed to load grants (${String((e as Error).message ?? e)}).`)
    } finally {
      setLoading(false)
    }
  }, [user.id])

  useEffect(() => { void refresh() }, [refresh])

  async function grant() {
    if (!pendingRole) return
    if (pendingResourceType && !pendingResourceId) {
      setError('Provide a resource ID for the chosen scope, or use Platform-wide.')
      return
    }
    setGranting(true)
    setError(null)
    try {
      await adminClient.grantUserRole(user.id, {
        role: pendingRole,
        resource_type: pendingResourceType || null,
        resource_id: pendingResourceType ? pendingResourceId : null,
      })
      setPendingRole('')
      setPendingResourceType('')
      setPendingResourceId('')
      await refresh()
      await onChanged()
    } catch (e) {
      setError(`Grant failed (${String((e as Error).message ?? e)}).`)
    } finally {
      setGranting(false)
    }
  }

  async function revoke(grantId: string) {
    if (!confirm('Revoke this role grant?')) return
    try {
      await adminClient.revokeUserRole(user.id, grantId)
      await refresh()
      await onChanged()
    } catch (e) {
      setError(`Revoke failed (${String((e as Error).message ?? e)}).`)
    }
  }

  return (
    <Modal
      title={`Roles · ${user.email}`}
      onClose={onClose}
      width={580}
      footer={<button style={btnGhost} onClick={onClose}>Close</button>}
    >
      {loading
        ? <div style={{ fontSize: 12, color: 'var(--t-2)' }}>Loading…</div>
        : (
          <>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Current grants</div>
            {grants.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--t-3)' }}>No role grants yet.</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--t-3)' }}>
                      <th style={th}>Role</th>
                      <th style={th}>Scope</th>
                      <th style={{ ...th, textAlign: 'right' }}>—</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((g) => (
                      <tr key={g.id} style={{ borderTop: '0.5px solid var(--bd-0)' }}>
                        <td style={td}>{g.role_name}</td>
                        <td style={td}>
                          {g.resource_type
                            ? <span title={g.resource_id ?? ''}>{g.resource_type}</span>
                            : <span style={{ color: 'var(--t-3)' }}>platform-wide</span>}
                          {g.resource_id ? <span style={{ color: 'var(--t-3)', marginLeft: 6 }}>·&nbsp;{g.resource_id.slice(0, 8)}…</span> : null}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <button style={btnLink} onClick={() => void revoke(g.id)}>Revoke</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '0.5px solid var(--bd-0)' }}>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Add a grant</div>

              <label style={labelStyle}>Role</label>
              <select value={pendingRole} onChange={(e) => setPendingRole(e.target.value)} style={inputStyle}>
                <option value="">— Select role —</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.name}>{r.name}</option>
                ))}
              </select>

              <label style={labelStyle}>Scope</label>
              <select
                value={pendingResourceType}
                onChange={(e) => setPendingResourceType(e.target.value as typeof pendingResourceType)}
                style={inputStyle}
              >
                {RESOURCE_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

              {pendingResourceType && (
                <>
                  <label style={labelStyle}>Resource ID</label>
                  <input
                    value={pendingResourceId}
                    onChange={(e) => setPendingResourceId(e.target.value)}
                    placeholder={`UUID of the ${pendingResourceType}`}
                    style={inputStyle}
                  />
                </>
              )}

              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  style={{ ...btnPrimary, opacity: pendingRole && !granting ? 1 : 0.6 }}
                  disabled={!pendingRole || granting}
                  onClick={() => void grant()}
                >
                  {granting ? 'Granting…' : 'Grant role'}
                </button>
              </div>
            </div>

            {error && <div style={{ marginTop: 12, fontSize: 12, color: 'var(--crit)' }}>{error}</div>}
          </>
        )}
    </Modal>
  )
}

const th: React.CSSProperties = { padding: '6px 0', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em' }
const td: React.CSSProperties = { padding: '6px 0', color: 'var(--t-1)' }
const btnLink: React.CSSProperties = {
  background: 'transparent', color: 'var(--crit)', border: 'none', fontSize: 12, cursor: 'pointer',
}
